import { ChangeDetectorRef, Component, ElementRef, inject, Input, PLATFORM_ID, ViewChild } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PropertyImage } from '../../models/property.model';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ViewContainerRef, TemplateRef, OnInit } from '@angular/core';

@Component({
  selector: 'app-property-gallery',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './property-gallery.html',
  styleUrl: './property-gallery.scss'
})
export class PropertyGalleryComponent implements OnInit {
  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);

  platformId = inject(PLATFORM_ID);
  @ViewChild('galleryContainer') galleryContainer!: ElementRef;
  @Input() images: PropertyImage[] = [];
  selectedIndex = 0;
  isManualFullscreen = false;
  @ViewChild('fullscreenTemplate') fullscreenTemplate!: TemplateRef<any>;
  private overlayRef?: OverlayRef;
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    if(isPlatformBrowser(this.platformId)) {
      this.initHammer();
    }
  }

  private async initHammer() {
  if (isPlatformBrowser(this.platformId)) {
    // Dynamically load HammerJS only in the browser
    const Hammer = (await import('hammerjs')).default; 
    
    if (this.galleryContainer?.nativeElement) {
      const mc = new Hammer(this.galleryContainer.nativeElement);
      
      // Hammer needs to know which directions to allow
      mc.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });

      mc.on("swipeleft swiperight", (ev: any) => {
        console.log(`${ev.type} gesture detected!`);
        // Trigger manual change detection if this updates a variable
        if (ev.type === 'swipeleft') {
          this.nextImage();
        } else if (ev.type === 'swiperight') {
          this.prevImage();   
        }
        this.cdr.detectChanges();
      });
    }
  }
}

  onSwipeLeft() {
    this.nextImage();
  }

  onSwipeRight() {
    this.prevImage();
  }

  get currentImage(): PropertyImage | undefined {
    return this.images[this.selectedIndex];
  }

  openManualFullscreen() {
    this.overlayRef = this.overlay.create({
      height: '100dvh', // Use dynamic viewport for iOS
      width: '100vw',
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    const portal = new TemplatePortal(this.fullscreenTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  closeManualFullscreen() {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  toggleFullScreen() {
    const elem = this.galleryContainer.nativeElement as any;
    const doc = document as any;
    try {
      // Check for any version of "is fullscreen"
      const isFullScreen = doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;

      if (!isFullScreen) {
        // Attempt to enter fullscreen
        if (elem.requestFullscreen) {
          elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          elem.webkitRequestFullscreen(); // Safari/iOS
        } else if (elem.msRequestFullscreen) {
          elem.msRequestFullscreen(); // IE11
        } else if (elem.webkitEnterFullscreen) {
          elem.webkitEnterFullscreen(); // Specialized for some iOS versions
        }
      } else {
        // Exit fullscreen
        if (doc.exitFullscreen) {
          doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) {
          doc.msExitFullscreen();
        }
      }
    } catch (e) {
      // If native fails (common on iPhone), use the CSS fallback
      this.isManualFullscreen = !this.isManualFullscreen;

      if (this.isManualFullscreen) {
        document.body.style.overflow = 'hidden'; // Prevent scrolling
      } else {
        document.body.style.overflow = 'auto';
      }
    }

  }

  selectImage(index: number): void {
    this.selectedIndex = index;
  }

  nextImage(): void {
    if (this.images.length > 0) {
      this.selectedIndex = (this.selectedIndex + 1) % this.images.length;
    }
  }

  prevImage(): void {
    if (this.images.length > 0) {
      this.selectedIndex = (this.selectedIndex - 1 + this.images.length) % this.images.length;
    }
  }
}